import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { MedicalSchoolMasterItem } from '@/types/settings-api';

/**
 * Medical school master search — PROD-761 commit 10 (Qualifications picker).
 *
 * GET /api/Master/MedicalSchools?searchQuery={q}
 *
 * Source for the Educational → Qualifications UNIVERSITY picker
 * (medical_school_list_widget.dart / edit_medical_school_widget.dart).
 * Master entries carry PascalCase `Id` + `MedicalSchool` (not a CodeList
 * envelope). Consumers map:
 *   - `MedicalSchoolMasterItem.Id`            → `EducationQualification.issuer`
 *   - `MedicalSchoolMasterItem.MedicalSchool` → `EducationQualification.issuerName`
 *
 * Wire-shape verified against live dev API 2026-05-14:
 *   [{ "Id": "301c8dda-…", "MedicalSchool": "Gulf Medical University, Ajman" }]
 *
 * Caller debounces 250ms (parity with all other typeaheads in this
 * module). Empty query short-circuits — the catalogue is bounded but
 * still not worth fetching untyped.
 */
export function useMedicalSchoolsSearch(searchQuery: string) {
  const trimmed = searchQuery.trim();
  return useQuery({
    queryKey: ['settings', 'master', 'medical-schools', trimmed],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({ searchQuery: trimmed });
      return get<MedicalSchoolMasterItem[]>(
        `/api/Master/MedicalSchools?${qs.toString()}`,
        { signal },
      );
    },
    enabled: trimmed.length > 0,
    staleTime: 60_000,
  });
}
